import { useRef } from 'react'
import { t } from './sprache'
import type { Zelle } from './Aufriss'
import { Dialogfenster } from './Dialogfenster'
import { Kfrregeln } from './Kfreigenschaften'
import { Positionskonten } from './Positionskonten'
import { Kontogruppe } from './Kontogruppe'
import { Stammdaten } from './Stammdaten'
import { Stichtagskursanpassung } from './Stichtagskursanpassung'

/** Welches Stammdatum ein Eigenschaften-Fenster zeigt (R858). */
export type Eigenschaftsziel = {
  objektart: string
  schluessel: string
  /** Was in der Kopfzeile neben *Eigenschaften* steht — Nummer und Name, wo die Zeile sie kennt. */
  titel?: string
}

/**
 * **Was eine Berichtszeile ist** — das Ziel für *Eigenschaften* (R858).
 *
 * Die Positionszeile ist ihre Position. Eine aufgeklappte Zeile ist das, was
 * **die letzte Ebene** neu hinzugebracht hat: Unter *Konto 0210 → Gesellschaft
 * Tochter 1* trägt die Zelle Konto und Gesellschaft, gemeint ist die
 * Gesellschaft. Ein Wert *Default* ist kein Stammdatum und bekommt kein Fenster.
 *
 * **Der Partner ist eine Gesellschaft**, wo er eine ist (`EH-…`), sonst ein
 * Partnercode (R490); eine Analysedimension trägt Dimensionswerte, das Segment
 * hat seine eigene Objektart.
 */
export function zielVon(objektart: string, schluessel: string, pfad: Zelle[]): Eigenschaftsziel | null {
  if (pfad.length === 0) {
    return { objektart, schluessel }
  }

  const letzte = pfad[pfad.length - 1]!
  const davor: Zelle = pfad[pfad.length - 2] ?? { einheit: null, konto: null }

  if (letzte.konto && letzte.konto !== davor.konto) {
    return { objektart: 'konto', schluessel: letzte.konto }
  }

  if (letzte.einheit && letzte.einheit !== davor.einheit) {
    return { objektart: 'einheit', schluessel: letzte.einheit }
  }

  for (const [achse, wert] of Object.entries(letzte.weitere ?? {})) {
    if (!wert || davor.weitere?.[achse] === wert) {
      continue
    }
    if (achse === 'partner') {
      return { objektart: wert.startsWith('EH-') ? 'einheit' : 'partnercode', schluessel: wert }
    }
    return { objektart: achse === 'segment' ? 'segment' : 'dimensionswert', schluessel: wert }
  }

  return null
}

/**
 * **Rechtsklick → Eigenschaften** — das Fenster für jedes Stammdatum, das in
 * einem Bericht als Zeile steht (R857, R858, 7.3 Abschnitt 20).
 *
 * **Das Blatt ist das der Stammdatenpflege**, nur ohne die Liste davor —
 * dieselben Felder, Zwecksätze und Fassungen. Darunter steht, was die
 * Objektart mitbringt: bei einer Zeile der Kapitalflussrechnung *Was in diese
 * Zeile läuft*, bei einer Position *Welche Konten hier stehen*.
 */
export function Eigenschaftsfenster({
  ziel,
  periode,
  aufSchliessen,
}: {
  ziel: Eigenschaftsziel
  /** Die Periode des Berichts — der Vorschlag für ein Umhängen ab Periode (R860). */
  periode?: number
  /** `geaendert` sagt, ob im Fenster geschrieben wurde — dann rechnet der Bericht neu. */
  aufSchliessen: (geaendert: boolean) => void
}) {
  // Ein Verweis und kein Zustand: Er ändert nichts am Bild, er wird nur beim
  // Schließen gelesen.
  const geaendert = useRef(false)
  const merke = () => {
    geaendert.current = true
  }

  return (
    <Dialogfenster
      titel={ziel.titel ? t('kfreigenschaften.titel', { zeile: ziel.titel }) : t('menue.eigenschaften')}
      weite="breit"
      aufSchliessen={() => aufSchliessen(geaendert.current)}
    >
      <Stammdaten
        zulaessig={[ziel.objektart]}
        vorauswahl={{ objektart: ziel.objektart, schluessel: ziel.schluessel }}
        nurBlatt
        // Die Zeile der Kapitalflussrechnung zeigt nur Bezeichnungen und was
        // sie zieht (R1328): Bereich, Art, Standard und Reihenfolge leitet CoKo
        // aus der Stelle in der Struktur ab.
        ohneEigenschaften={ziel.objektart === 'kfr_zeile' || ziel.objektart === 'kfr_abschnitt'}
        // **Die Nummer steht im Fenster und ist änderbar** (2.8 Abschnitt
        // 13.41, R1333, R1336) — bei einer Zeile und bei einem Abschnitt.
        mitNummer={ziel.objektart === 'kfr_zeile' || ziel.objektart === 'kfr_abschnitt'}
        // **Gelöscht wird eine Zeile nur über den Rechtsklick** (R1334), der
        // aufräumt; ein Abschnitt gar nicht (R1333).
        ohneAussondern={ziel.objektart === 'kfr_zeile' || ziel.objektart === 'kfr_abschnitt'}
        ohneAnlegen={[ziel.objektart]}
        aufAenderung={merke}
        zusatz={(blatt) =>
          ziel.objektart === 'kfr_zeile' ? (
            <Kfrregeln zeile={blatt.schluessel} aufAenderung={merke} />
          ) : ziel.objektart === 'position' ? (
            <Positionskonten position={blatt.schluessel} periode={periode} aufAenderung={merke} />
          ) : ziel.objektart === 'konto' ? (
            <>
              {/* Der Haken „eigenes Konto je Quellkonto“ (2.6 Abschnitt 2.14, R1655). */}
              <Stichtagskursanpassung konto={blatt.schluessel} periode={periode} aufAenderung={merke} />
              {/* Die Gruppe gehört zur Zuordnung — ab Monat oder immer (R1648, R1649). */}
              {!blatt.abWerk && <Kontogruppe konto={blatt.schluessel} periode={periode} aufAenderung={merke} />}
            </>
          ) : null
        }
      />
    </Dialogfenster>
  )
}
